import { CheckCircleOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Input, List } from 'antd'
import { useState, type JSX, type ReactNode } from 'react'

import { Capability } from 'api/clients'
import { StyledModal } from 'shared/components/Modal'

type CapabilitySearchModalProps = {
  capabilities: Capability[]
  onAdd: (capability: Capability) => unknown
  onClose: () => void
  closable?: boolean
  footer?: ReactNode
  isError?: boolean
  isLoading?: boolean
  notice?: ReactNode
  renderTitle?: (capability: Capability) => ReactNode
}

export function CapabilitySearchModal({
  capabilities,
  onAdd,
  onClose,
  closable = true,
  footer = null,
  isError = false,
  isLoading = false,
  notice = null,
  renderTitle = (capability) => capability.name,
}: CapabilitySearchModalProps): JSX.Element {
  const [searchValue, setSearchValue] = useState('')
  const query = searchValue.toLowerCase()
  const matchingCapabilities = capabilities.filter((capability) =>
    `${capability.name}|||${capability.description || ''}`.toLowerCase().includes(query),
  )

  return (
    <StyledModal
      closable={closable}
      footer={footer}
      onCancel={onClose}
      open={true}
      style={{ paddingTop: '24px' }}
      title={
        <Input
          allowClear={true}
          autoFocus
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search Capabilities …"
          prefix={<SearchOutlined />}
          value={searchValue}
        />
      }
    >
      {notice}
      {isError ? (
        <Alert message="The capabilities could not be loaded." showIcon type="error" />
      ) : (
        <List
          dataSource={matchingCapabilities}
          loading={isLoading}
          locale={{ emptyText: 'No match found' }}
          renderItem={(capability) => (
            <List.Item
              actions={[
                <Button
                  aria-label={`Add ${capability.name}`}
                  onClick={() => onAdd(capability)}
                  shape="round"
                  size="small"
                  type="primary"
                >
                  Add
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={<CheckCircleOutlined />}
                description={capability.description || '(no description)'}
                title={renderTitle(capability)}
              />
            </List.Item>
          )}
          rowKey="id"
        />
      )}
    </StyledModal>
  )
}
